import type { ReactNode } from 'react';
import type { PlpSortByValue } from '@/features/plp/constants';
import { PlpBase } from '@/features/plp/presentation/components/PlpBase';
import { PincodePopup } from '@/shared/features/pincode/presentation/components/PincodePopup';
import { PlpScrollToTopButton } from '@/shared/widgets/PlpScrollToTopButton';
import type { SearchPlpShell } from '@/pages/product-listing/useSearchPlpShell';

type PlpBaseProps = React.ComponentProps<typeof PlpBase>;

export interface SearchPlpPageViewProps {
  shell: SearchPlpShell;
  sortBy: PlpSortByValue;
  plpBaseOverrides?: Partial<PlpBaseProps>;
  renderDesktopFilterSidebar?: PlpBaseProps['renderDesktopFilterSidebar'];
  children?: ReactNode;
}

export function SearchPlpPageView({
  shell,
  sortBy,
  plpBaseOverrides,
  renderDesktopFilterSidebar,
  children,
}: SearchPlpPageViewProps) {
  const {
    topSentinelRef,
    showScrollToTop,
    isDesktop,
    plp,
    plpAnalytics,
    displayListingMeta,
    breadcrumbItems,
    storeCountLabel,
    sidFromUrl,
    isNoStoreFound,
    pincode,
    handleOpenBottomPincode,
    handleActivateStoreDesigns,
    handleShowAllDesigns,
    pincodeProps,
  } = shell;

  return (
    <>
      <div ref={topSentinelRef} aria-hidden />
      <PlpBase
        {...plp}
        listingMeta={displayListingMeta}
        countLabel={storeCountLabel}
        currentSortBy={sortBy}
        pincode={pincode}
        onOpenPincode={handleOpenBottomPincode}
        openPdpInNewTab={isDesktop}
        onActivateStoreDesigns={handleActivateStoreDesigns}
        onShowAllDesigns={handleShowAllDesigns}
        isStoreDesignsActive={Boolean(sidFromUrl) || isNoStoreFound}
        isStorePlp={false}
        isStoreCitySelectionMode={false}
        selectedStoreData={null}
        selectedStoresData={[]}
        isStoreWidgetLoading={false}
        breadcrumbItems={breadcrumbItems}
        hasStoreLocationWidget={false}
        isNoStoreFound={isNoStoreFound}
        onProductClick={plpAnalytics.trackProductClick}
        onWishlistClick={plpAnalytics.trackWishlistClick}
        onWishlistToggle={plpAnalytics.trackWishlistToggle}
        onVideoClick={plpAnalytics.trackVideoClick}
        onVideoCallCartAdd={plpAnalytics.trackVideoCallCartAdd}
        onViewDetailsClick={plpAnalytics.trackViewDetailsClick}
        onProductViewed={plpAnalytics.trackListingPageProductViewed}
        viewedProductIds={plpAnalytics.viewedProductIds}
        renderDesktopFilterSidebar={renderDesktopFilterSidebar}
        {...plpBaseOverrides}
      />
      <PincodePopup {...pincodeProps} />
      {children}
      <PlpScrollToTopButton show={showScrollToTop} />
    </>
  );
}
